import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import '../../global.css';
import Navbar from '../../components/Navbar';
import SiteFooter from '../../components/SiteFooter';
import NeuralCanvas from '../../components/NeuralCanvas';
import { useSEO } from '../../hooks/useSEO';

const TOC = [
  ['overview', 'Overview'],
  ['collection', 'Data We Collect'],
  ['use', 'How We Use Data'],
  ['sharing', 'Data Sharing'],
  ['residency', 'African Data Residency'],
  ['cookies', 'Cookies'],
  ['rights', 'Your Rights'],
  ['security', 'Security'],
  ['contact', 'Contact'],
];

function Section({ id, num, title, children }) {
  return (
    <section id={id} style={{ marginBottom: 56, scrollMarginTop: 80 }}>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--accent)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 10 }}>{num}</div>
      <h2 style={{ fontSize: 22, fontWeight: 500, letterSpacing: '-0.02em', marginBottom: 16, color: 'var(--fg)' }}>{title}</h2>
      <div style={{ fontSize: 14, lineHeight: 1.82, color: 'var(--fg-mid)' }}>{children}</div>
    </section>
  );
}

function P({ children }) { return <p style={{ marginBottom: 14 }}>{children}</p>; }
function Ul({ items }) {
  return (
    <ul style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '12px 0' }}>
      {items.map(item => (
        <li key={item} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <div style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--fg-dim)', marginTop: 8, flexShrink: 0 }} />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export default function Legal() {
  useSEO({
    title: 'Privacy Policy',
    description: 'Cybernate\'s privacy policy: how we collect, use, and protect your data, including African data residency commitments.',
    path: '/legal',
  });
  const [active, setActive] = useState('overview');

  return (
    <>
      <NeuralCanvas />
      <div className="neural-page">
      <Navbar />

      <div style={{ borderBottom: '1px solid var(--line)', padding: '64px 0 48px', background: 'var(--bg)' }}>
        <div style={{ maxWidth: 'var(--maxw)', margin: '0 auto', padding: '0 var(--pad-x)' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--accent)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 16 }}>Legal</div>
          <h1 style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 400, letterSpacing: '-0.03em', marginBottom: 12 }}>Privacy Policy</h1>
          <p style={{ fontSize: 14, color: 'var(--fg-dim)', fontFamily: 'var(--font-mono)', letterSpacing: '0.06em' }}>
            Last updated: May 1, 2026 · Effective: May 1, 2026
          </p>
        </div>
      </div>

      <div className="legal-layout">

        <aside style={{ position: 'sticky', top: 80, paddingTop: 48 }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--fg-dim)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 14 }}>Contents</div>
          {TOC.map(([id, label]) => (
            <button key={id} onClick={() => { setActive(id); document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', borderRadius: 'var(--r-sm)', background: active === id ? 'var(--bg-2)' : 'transparent', border: 'none', fontSize: 13, color: active === id ? 'var(--fg)' : 'var(--fg-dim)', cursor: 'pointer', fontFamily: 'inherit', marginBottom: 2 }}>
              {label}
            </button>
          ))}
          <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--line)' }}>
            <Link to="/terms" style={{ fontSize: 13, color: 'var(--fg-dim)', display: 'block', marginBottom: 8 }}>Terms of Service →</Link>
            <a href="mailto:info@cybernate.ai" style={{ fontSize: 13, color: 'var(--accent)' }}>Contact us</a>
          </div>
        </aside>

        <div style={{ paddingTop: 56, paddingBottom: 80 }}>

          <Section id="overview" num="00 :: Overview" title="Overview">
            <P>Cybernate AI ("Cybernate", "we", "us") operates developer infrastructure across Africa. This Privacy Policy explains how we collect, use, and protect your personal information when you use our platform, APIs, and related services.</P>
            <P>We are committed to African data sovereignty and comply with the Nigeria Data Protection Regulation (NDPR), the South African POPIA, and Kenya's Data Protection Act where applicable.</P>
          </Section>

          <Section id="collection" num="01 :: Collection" title="Data We Collect">
            <P>We collect information in the following categories:</P>
            <Ul items={[
              'Account data: email address, name, company, and the type of work you are doing',
              'Usage data: how you use the platform, your activity history, and error reports',
              'Billing data: transaction history, payment method (processed by Paystack/Flutterwave, not stored by us)',
              'Technical data: IP addresses, device type, browser version',
              'Content data: inputs and outputs processed on the platform (retained for 30 days by default)',
            ]} />
          </Section>

          <Section id="use" num="02 :: Use" title="How We Use Data">
            <Ul items={[
              'Delivering API services and processing your requests',
              'Calculating usage and generating invoices',
              'Monitoring platform health, uptime, and security incidents',
              'Improving model quality through aggregated, anonymized feedback (opt-in only)',
              'Sending transactional emails (API key creation, billing alerts, security notifications)',
              'Complying with applicable African data protection laws',
            ]} />
          </Section>

          <Section id="sharing" num="03 :: Sharing" title="Data Sharing">
            <P>We do not sell your personal data. We share data only with:</P>
            <Ul items={[
              'Infrastructure providers (AWS, hosted in-region) for compute and storage',
              'Payment processors (Paystack, Flutterwave) for billing',
              'Analytics providers (self-hosted, in-region) for platform monitoring',
              'Law enforcement, when required by valid African legal process',
            ]} />
          </Section>

          <Section id="residency" num="04 :: Residency" title="African Data Residency">
            <P>All customer data is stored and processed within African infrastructure. Your region selection at signup determines your data residency:</P>
            <Ul items={[
              'Lagos, Nigeria — Nigeria data protection law (NDPR)',
              'Nairobi, Kenya — Kenya Data Protection Act',
              'Johannesburg, South Africa — South Africa data protection law (POPIA)',
              'Accra, Ghana — Ghana Data Protection Act (coming Q3 2026)',
            ]} />
            <P>No customer data is ever routed through or stored in European or North American infrastructure.</P>
          </Section>

          <Section id="cookies" num="05 :: Cookies" title="Cookies">
            <P>We use strictly necessary cookies for session management and authentication. We do not use third-party advertising or tracking cookies.</P>
          </Section>

          <Section id="rights" num="06 :: Rights" title="Your Rights">
            <P>Under applicable African data protection law, you have the right to:</P>
            <Ul items={[
              'Access the personal data we hold about you',
              'Correct inaccurate data',
              'Request deletion of your account and associated data',
              'Export your data in a portable format',
              'Opt out of non-essential data processing',
            ]} />
            <P>To exercise these rights, email <a href="mailto:info@cybernate.ai" style={{ color: 'var(--accent)' }}>info@cybernate.ai</a>. We respond within 7 business days.</P>
          </Section>

          <Section id="security" num="07 :: Security" title="Security">
            <P>Your data is encrypted when stored and encrypted when being transferred. Access credentials are never stored in plain text. We conduct regular security audits and operate a responsible disclosure program at <a href="mailto:security@cybernate.ai" style={{ color: 'var(--accent)' }}>security@cybernate.ai</a>.</P>
          </Section>

          <Section id="contact" num="08 :: Contact" title="Contact">
            <P>For privacy questions or data requests, contact our Data Protection Officer:</P>
            <div style={{ padding: '16px 20px', background: 'var(--bg-1)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)', fontSize: 13, color: 'var(--fg-mid)', lineHeight: 1.7 }}>
              <div>Cybernate AI — Data Protection Officer</div>
              <div><a href="mailto:info@cybernate.ai" style={{ color: 'var(--accent)' }}>info@cybernate.ai</a></div>
              <div>Lagos, Nigeria</div>
            </div>
          </Section>

        </div>
      </div>

      <SiteFooter />
      </div>
    </>
  );
}
